import { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Gift, 
  DollarSign, 
  Percent, 
  Copy, 
  Check,
  Sparkles,
  Users,
  ArrowRight,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";
import { SharedHeader } from "@/components/SharedHeader";

const ReferralProgram = () => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showFullDetails, setShowFullDetails] = useState(false);

  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const referralLink = "https://manage.call24x7.ai/signup?ref=YOUR_CODE";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success(t("referral.linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSignupRedirect = () => {
    window.location.href = "https://manage.call24x7.ai/signup";
  };

  const shareOptions = [
    { name: "WhatsApp", color: "bg-green-500 hover:bg-green-600", icon: "📱" },
    { name: "Telegram", color: "bg-blue-500 hover:bg-blue-600", icon: "✈️" },
    { name: "Facebook", color: "bg-blue-600 hover:bg-blue-700", icon: "📘" },
    { name: "X", color: "bg-black hover:bg-gray-800", icon: "𝕏" },
    { name: "LinkedIn", color: "bg-blue-700 hover:bg-blue-800", icon: "💼" },
    { name: "Reddit", color: "bg-orange-500 hover:bg-orange-600", icon: "🔴" },
    { name: "Email", color: "bg-gray-600 hover:bg-gray-700", icon: "📧" },
    { name: "SMS", color: "bg-green-600 hover:bg-green-700", icon: "💬" },
  ];

  const additionalShare = [
    { name: "TikTok", icon: "🎵" },
    { name: "Instagram", icon: "📷" },
    { name: "Snapchat", icon: "👻" },
    { name: "Discord", icon: "🎮" },
  ];

  return (
    <>
      <Helmet>
        <title>{t("referral.pageTitle")} | Call24x7.AI</title>
        <meta name="description" content="Share Call24x7.ai with friends and earn rewards! Get $1 per signup and 5% of every topup your referrals make." />
        <meta name="keywords" content="referral program, earn rewards, AI customer support referral, Call24x7 referral" />
        <link rel="canonical" href="https://call24x7.ai/referral" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5" dir={isRTL ? 'rtl' : 'ltr'}>
        <RotatingNewsBanner />
        <SharedHeader />

        {/* Hero Section */}
        <section className="pt-36 pb-12 px-4">
          <div className="w-full max-w-6xl mx-auto text-center">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 text-sm px-4 py-1">
              <Gift className="w-4 h-4 mr-2 inline" />
              {t("referral.title")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              {t("referral.heroTitle")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("referral.heroSubtitle")}
            </p>
          </div>
        </section>

        {/* Rewards Cards */}
        <section className="py-8 px-4">
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {/* New User Gets */}
              <Card className="text-center border-2 border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                <CardHeader className="pb-2">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
                    <Gift className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle className="text-lg text-muted-foreground">{t("referral.newUserGets")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-5xl font-bold text-green-600 mb-2">$5</div>
                  <p className="text-sm text-muted-foreground">{t("referral.freeCredits")}</p>
                </CardContent>
              </Card>

              {/* You Get */}
              <Card className="text-center border-2 border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                <CardHeader className="pb-2">
                  <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-2">
                    <DollarSign className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg text-muted-foreground">{t("referral.youGet")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-5xl font-bold text-blue-600 mb-2">$1</div>
                  <p className="text-sm text-muted-foreground">{t("referral.perSignup")}</p>
                </CardContent>
              </Card>

              {/* Ongoing Reward */}
              <Card className="text-center border-2 border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                <CardHeader className="pb-2">
                  <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-2">
                    <Percent className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg text-muted-foreground">{t("referral.ongoingReward")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-5xl font-bold text-purple-600 mb-2">5%</div>
                  <p className="text-sm text-muted-foreground">{t("referral.ofEveryTopup")}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Referral Link Section - Requires Signup */}
        <section className="py-8 px-4">
          <div className="w-full max-w-4xl mx-auto">
            {!showFullDetails ? (
              <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 mx-auto text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">{t("referral.getYourLink")}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t("referral.signupToGetLink")}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" onClick={handleSignupRedirect} className="gap-2">
                      {t("referral.signUpNow")} <ArrowRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={() => window.location.href = "https://manage.call24x7.ai/login"}
                    >
                      {t("referral.alreadyHaveAccount")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    {t("referral.yourReferralLink")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={referralLink}
                      readOnly
                      className="flex-1 px-4 py-3 rounded-lg border border-border bg-muted/50 text-sm"
                    />
                    <Button onClick={handleCopyLink} className="gap-2 px-6">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? t("referral.copied") : t("referral.copy")}
                    </Button>
                  </div>

                  {/* Share buttons */}
                  <div>
                    <p className="text-sm font-medium mb-3">{t("referral.share")}</p>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                      {shareOptions.map((option) => (
                        <Button
                          key={option.name}
                          variant="outline"
                          className="flex flex-col gap-1 h-auto py-3 hover:scale-105 transition-transform"
                        >
                          <span className="text-lg">{option.icon}</span>
                          <span className="text-[10px]">{option.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 px-4">
          <div className="w-full max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 border-none">
              <CardContent className="py-8 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-4">{t("referral.itsAutomatic")}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t("referral.automaticDescription")}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Additional Share Options */}
        <section className="py-8 px-4">
          <div className="w-full max-w-4xl mx-auto text-center">
            <p className="text-muted-foreground mb-4">
              {t("referral.copyToPaste")}
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              {additionalShare.map((option) => (
                <Button
                  key={option.name}
                  variant="ghost"
                  className="flex flex-col gap-1 h-auto py-3"
                  onClick={handleCopyLink}
                >
                  <span className="text-2xl">{option.icon}</span>
                  <span className="text-xs text-muted-foreground">{option.name}</span>
                </Button>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="mt-6 gap-2"
              onClick={() => {
                const message = "Check out Call24x7.AI - AI-powered customer support! Sign up using my link and get $5 free credits: " + referralLink;
                navigator.clipboard.writeText(message);
                toast.success(t("referral.messageCopied"));
              }}
            >
              <Copy className="w-4 h-4" />
              {t("referral.copyMessageLink")}
            </Button>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="w-full max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">{t("referral.startEarning")}</h2>
            <p className="text-muted-foreground mb-8">
              {t("referral.startEarningDesc")}
            </p>
            <Button size="lg" onClick={handleSignupRedirect} className="gap-2 px-8">
              {t("referral.getStarted")} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-border/50">
          <div className="w-full max-w-6xl mx-auto text-center text-sm text-muted-foreground">
            <p>© 2024 Call24x7.AI - All rights reserved</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/" className="hover:text-primary transition-colors">{t("footer.home")}</Link>
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">{t("footer.privacy")}</Link>
              <Link to="/blog" className="hover:text-primary transition-colors">{t("footer.blog")}</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ReferralProgram;
